import { useFormContext } from "react-hook-form";
import { type ISection } from "src/interfaces";
import { useSelector } from "src/redux/hooks";
import { convertFromUUIDKeys } from "src/redux/utils/convertFromUUIDKeys";
import { insertValues } from "src/redux/utils/insertValues";

import { isBlankRow, removeBlankRows } from "../utils/blankRowUtils";

export { isBlankRow, removeBlankRows };

export const convertLbEntryForSave = (
  lbentry: Record<string, any>,
  values: Record<string, any>,
  manualSave: boolean = false,
  removeBlankRowsOnContinuousEntry: boolean = false,
) => {
  const modifiedLogbook = insertValues(values, lbentry.sections, manualSave);
  const cleanedLogbook =
    removeBlankRowsOnContinuousEntry && lbentry.continousEntry
      ? removeBlankRows(modifiedLogbook)
      : { sections: modifiedLogbook, removedBlankRows: false };

  const originalLogbookVersion = convertFromUUIDKeys(
    cleanedLogbook.sections as ISection[],
  );
  const objToSend = {
    ...lbentry,
    sections: originalLogbookVersion,
  };

  return {
    payload: JSON.stringify(objToSend),
    removedBlankRows: cleanedLogbook.removedBlankRows,
  };
};

/**
 * Custom hook to convert logbook entry for saving.
 *
 * @param manualSave - An optional boolean indicating whether the save is manual.
 * @returns A function to convert logbook entry data into a JSON string for saving.
 */
export const useConvertLbEntry = (manualSave: boolean = false) => {
  const { lbentry } = useSelector(state => state.generatorSlice);
  const { getValues } = useFormContext();
  /**
   * Converts logbook entry data into a JSON string.
   *
   * @param data - An optional object containing the logbook entry data to convert.
   * @returns A JSON string representing the converted logbook entry.
   */
  const convertLbEntry = (data?: Record<string, any>) => {
    const values = data ?? getValues();
    return convertLbEntryForSave(lbentry, values, manualSave).payload;
  };

  return convertLbEntry;
};
